/**
 * The studio's text field: one line tall until the writing needs more.
 *
 * Every block editor used to size its own field, and they were all wrong in the
 * same two ways. A `multiline` `TextInput` opens at whatever the platform
 * decides — on the web a `<textarea>` is **two rows tall** before a character is
 * typed — and then at whatever `minHeight` the style table asked for, so a
 * freshly added block greeted the creator with an empty box two to three lines
 * high. And it did not follow the writing: once the text passed the box, the box
 * stayed put and the text scrolled, so the one thing on screen not sized to the
 * writing was the field holding it.
 *
 * So the field owns its height here. At rest it is `minLines` — one, unless a
 * caller knows the writing there is always longer — and it grows line by line
 * with the content up to `maxHeight`, past which it scrolls. That is the honest
 * end of it: there is now more writing than the field can show at once.
 *
 * `minHeight` in the caller's style is deliberately superseded, because that
 * number *is* the bug this replaces; a field with more room to start in asks for
 * `minLines`, which is counted in lines of its own writing. `maxHeight` is
 * honoured, because a cap is a real decision about the block's shape.
 *
 * The two platforms are measured by their own means rather than by a shared
 * trick. On a device `onContentSizeChange` already reports the writing's own
 * height, which is the number wanted. On the web `scrollHeight` is the only
 * reading available, and it never reports less than the box it sits in — so a
 * field that only ever grew could never shrink again — which is why the box is
 * collapsed for the length of one read and the content is measured from there.
 */
import {type Ref, useCallback, useLayoutEffect, useRef, useState} from 'react'
import {
  type NativeSyntheticEvent,
  StyleSheet,
  TextInput,
  type TextInputContentSizeChangeEventData,
  type TextInputProps,
  type TextStyle,
} from 'react-native'

import {IS_WEB} from '#/env'
import {useFirstFieldFocus} from './StudioFirstFieldFocus'

/** A DOM text area, as far as this module needs to know it. */
type MeasurableTextArea = {style: {height: string}; scrollHeight: number}

/**
 * A style length as a plain number.
 *
 * A React Native style can hold a percentage, an animated node or nothing at
 * all, and none of those can be added up — the field only ever measures in px,
 * so anything that is not a number counts as nothing.
 */
function px(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

export type StudioTextFieldProps = Omit<
  TextInputProps,
  // The field is always multiline, always one row to start with, and always
  // measured here — a caller asking for any of the three would be asking to put
  // the box back in charge of its own height, which is the bug this replaces.
  'multiline' | 'numberOfLines' | 'onContentSizeChange'
> & {
  /** The quiet height, counted in lines of the field's own writing. */
  minLines?: number
  /** Where growth stops and the field begins to scroll instead. */
  maxHeight?: number
  /**
   * The underlying input, for the caller that has to put the caret in it — the
   * formula keyboard, which hands focus back after a symbol is inserted.
   *
   * Forwarded rather than dropped: the field measures with its own reference, so
   * a caller's would be lost if they were not merged.
   */
  ref?: Ref<TextInput>
}

export function StudioTextField({
  style,
  minLines = 1,
  maxHeight,
  ref,
  ...rest
}: StudioTextFieldProps) {
  const node = useRef<TextInput>(null)
  /** Both refs, because the field measures with one and a caller may want one. */
  const setNode = useCallback(
    (instance: TextInput | null) => {
      node.current = instance
      if (typeof ref === 'function') ref(instance)
      else if (ref) ref.current = instance
    },
    [ref],
  )
  /**
   * The writing's own height, in px, once it has been measured.
   *
   * A *border-box* height, because that is the number the field is handed back:
   * a React Native style sizes the element including its border. Neither reader
   * reports that. The DOM's `scrollHeight` stops at the padding, and a device's
   * `contentSize` does too, so the two hairline pixels are added to each reading
   * where it is taken. Without them a field sat exactly two pixels short of its
   * own last line: the writing was clipped, and by an amount too small to look
   * like anything but a rendering quirk.
   *
   * `null` until then, which is what makes the *first* frame a one-line box
   * instead of whatever a textarea feels like being: a field must not open tall
   * and then shrink when it measures itself.
   */
  const [content, setContent] = useState<number | null>(null)

  /*
   * A block the creator has just added owes them the caret, and this is where
   * most blocks pay it: this field is the first thing in nearly every editor.
   * The fields that are not this component take the caret the same way (see
   * `#/components/studio/StudioFirstFieldFocus`), and only the first field of
   * the new block is ever served — so a body field, an answer option or a later
   * cell stays out of it.
   */
  useFirstFieldFocus(node)

  const flat = StyleSheet.flatten(style) as TextStyle | undefined
  const lineHeight =
    px(flat?.lineHeight) || Math.round((px(flat?.fontSize) || 14) * 1.3)
  const vertical = px(flat?.paddingVertical)
  const verticalPadding = vertical
    ? vertical * 2
    : px(flat?.paddingTop) + px(flat?.paddingBottom)
  const width = px(flat?.borderWidth)
  const border = width
    ? width * 2
    : px(flat?.borderTopWidth) + px(flat?.borderBottomWidth)

  /** `minLines` lines of this field's writing, and no more. */
  const resting = Math.round(
    lineHeight * Math.max(1, minLines) + verticalPadding + border,
  )
  /** A caller's own `maxHeight` is still the cap; it is just not the height. */
  const cap = maxHeight ?? (px(flat?.maxHeight) || Number.POSITIVE_INFINITY)
  const height =
    content == null ? resting : Math.min(Math.max(content, resting), cap)
  const full = height >= cap

  /** The web reading: from a box that is not standing in the writing's way. */
  const measureFromTheDom = useCallback(() => {
    const field = node.current as unknown as
      | (MeasurableTextArea & {placeholder: string})
      | null
    if (!field || typeof field.scrollHeight !== 'number') return
    // The placeholder is part of what a textarea reports as its content — a
    // placeholder long enough to wrap made an empty field two lines tall, which
    // is the exact shape this component exists to get rid of. It is put back
    // before the frame is painted, so nobody sees it go.
    const placeholder = field.placeholder
    const committed = field.style.height
    if (placeholder) field.placeholder = ''
    field.style.height = 'auto'
    const measured = field.scrollHeight + border
    field.style.height = committed
    if (placeholder) field.placeholder = placeholder
    setContent(previous => (previous === measured ? previous : measured))
  }, [border])

  // Re-read whenever the writing changes — which includes the first render, when
  // an existing draft's text has to be measured just as freshly typed text is.
  useLayoutEffect(() => {
    if (IS_WEB) measureFromTheDom()
  }, [measureFromTheDom, rest.value, rest.defaultValue])

  const handleContentSize = useCallback(
    (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      setContent(Math.ceil(event.nativeEvent.contentSize.height) + border)
    },
    [border],
  )

  return (
    <TextInput
      ref={setNode}
      multiline
      // A web `<textarea>` is two rows tall before it is given a height, and the
      // collapse above can only ever measure *down* to that. One row is the
      // floor the whole thing is built on; on a device it is not read.
      numberOfLines={IS_WEB ? 1 : undefined}
      {...rest}
      style={[
        // Last, so the resting height wins over a `minHeight` the style table
        // still carries from when the box was fixed.
        style,
        {
          height,
          minHeight: resting,
          maxHeight: cap === Number.POSITIVE_INFINITY ? undefined : cap,
          ...(IS_WEB ? {overflow: full ? 'scroll' : 'hidden'} : null),
        },
      ]}
      onContentSizeChange={IS_WEB ? undefined : handleContentSize}
    />
  )
}
